import { useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

interface MessageInputProps {
    textInputRef: any;
    newMessage: string;
    handleInputChange: (e: any) => void;
    handleKeyDown: (e: any) => void;
    handlePaste: (e: any) => void;
    numOfLines: number;
    i18n: any;
    isPreviewPrompt?: boolean;
    showPiiText?: boolean;
    onTextareaClick?: () => void;
}

export default function MessageInput({
    textInputRef,
    newMessage,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    numOfLines,
    i18n,
    isPreviewPrompt = false,
    showPiiText = false,
    onTextareaClick,
}: MessageInputProps) {
    const getPiiTypeColor = (type: string): string => {
        switch (type.toUpperCase()) {
            case 'CREDIT_CARD':
                return 'bg-blue-400';
            case 'IBAN_CODE':
                return 'bg-green-400';
            case 'CRYPTO':
                return 'bg-yellow-400';
            case 'PHONE_NUMBER':
                return 'bg-purple-400';
            case 'EMAIL_ADDRESS':
                return 'bg-red-400';
            case 'PERSON':
                return 'bg-orange-400';
            case 'IP_ADDRESS':
                return 'bg-pink-400';
            case 'NRP':
                return 'bg-teal-400';
            case 'LOCATION':
                return 'bg-indigo-400';
            case 'REGEX':
                return 'bg-slate-400';
            default:
                return 'bg-gray-400';
        }
    };

    const renderPiiText = (text: string) => {
        const parts = text.split(/(\〖[^〗]+〗)/);
        return parts.map((part, index) => {
            if (part.startsWith('〖') && part.endsWith('〗')) {
                const [value, type] = part.slice(1, -1).split('⟆');
                const bgColorClass = getPiiTypeColor(type);
                return (
                    <span key={index} className={`${bgColorClass} text-gray-900 rounded-sm px-0.5 mx-[0.5px]`}>
                        {value} &laquo;{type}&raquo;
                    </span>
                );
            }
            return (
                <span key={index} className="whitespace-pre-wrap">
                    {part}
                </span>
            );
        });
    };

    return (
        <div className="relative w-full" onClick={onTextareaClick}>
            {showPiiText && (
                <div
                    className="absolute left-0 top-0 w-full max-h-full overflow-y-auto sm:pl-6 sm:pr-6 py-4 pl-4 pr-4 select-none"
                    style={{ fontFamily: 'inherit', width: '100%' }}
                >
                    {renderPiiText(newMessage)}
                </div>
            )}
            <TextareaAutosize
                ref={textInputRef}
                minRows={1}
                maxRows={4}
                autoFocus={!isPreviewPrompt && !showPiiText}
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                readOnly={showPiiText}
                placeholder={i18n.translate({ id: 'enterMessage', sourceName: 'Chat' })}
                style={{ fontFamily: 'inherit', width: '100%' }}
                className={`${
                    isPreviewPrompt ? 'bg-input-backgroundColor' : 'bg-chatInput-background'
                }  text-chatInput-textColor ${
                    showPiiText ? 'text-opacity-0' : 'text-opacity-100'
                } placeholder:text-chatInput-playceHolderColor sm:pl-6 sm:pr-6 py-4 pl-4 pr-4 m-auto flex-1
                border border-chatInput-borderColor
                sm:border-none
                text-md
                resize-none
                sm:focus:border-transparent
                focus:border-chatInput-borderColor
                grow cursor-text focus-visible:outline-none
                !outline-none
                sm:w-full
                transition-[width,height,border-radius,background-color,border] duration-[300ms] ${
                    numOfLines > 2 ? 'rounded-lg sm:rounded-[1rem]' : 'rounded-lg sm:rounded-[2rem]'
                }`}
            ></TextareaAutosize>
        </div>
    );
}
